import { useState } from 'react';
import { Container } from 'roo-ui/components';
import TwoColumnWithBookingSummary from 'components/Layout/TwoColumnWithBookingSummary';
import CancellationPolicyModal from 'components/Modals/CancellationPolicyModal';
import { useScrollTopOnMount, useModal } from 'lib/hooks';
import RequestReviewInfo from './RequestReviewInfo';
import NewStayDates from './NewStayDates';
import PaymentAdjustmentDetails from './PaymentAdjustmentDetails';
import ButtonGroup from 'components/ButtonGroup';
import { FeesDisclaimer, VoucherDisclaimer, RefundDisclaimer } from 'components/Disclaimers';
import TermsAndConditionsAgreement from '../TermsAndConditionsAgreement';
import HorizontalRule from 'components/Layout/HorizontalRule';
import * as track from 'lib/gaTracking/changeDatesPageEvents';
import { useChangeDates } from '../ChangeDatesProvider';
import CallToChange from './CallToChange';
import toNumber from 'lodash/toNumber';

const ReviewQuote = () => {
  useScrollTopOnMount();

  const {
    openModal,
    closeModal,
    modalProps: { isOpen: isCancellationPolicyModalOpen },
  } = useModal();

  const { amendment, startDate, endDate, errorCode, handleAccept, handleBackToEdit } = useChangeDates();
  const [isTermsAccepted, setTermsAccepted] = useState(false);

  if (!amendment) return null;

  const { type } = amendment.amendmentTotal ?? {};

  const showDisclaimer = type === 'REFUND_PAYMENT' || toNumber(amendment.payableAtProperty?.total.amount) > 0;

  const continueLabel = type === 'ADDITIONAL_PAYMENT' ? 'Confirm & pay' : type === 'REFUND_PAYMENT' ? 'Confirm & refund' : 'Confirm';

  const continueButtonDisabled = !isTermsAccepted;

  const handleTopCancellationPolicyButtonClick = () => {
    track.openCancellationPolicy('Top');
    openModal();
  };

  const handleBottomCancellationPolicyButtonClick = () => {
    track.openCancellationPolicy('Bottom');
    openModal();
  };

  const handleTermsAndConditionsCheckboxChange = (checked: boolean) => {
    track.termsAndConditionsCheckbox(checked);
    setTermsAccepted(checked);
  };

  return (
    <>
      <CancellationPolicyModal
        rawDescription={amendment.cancellationPolicy?.description ?? ''}
        isOpen={isCancellationPolicyModalOpen}
        onRequestClose={closeModal}
      />
      <TwoColumnWithBookingSummary heading="Review your date change">
        <RequestReviewInfo />
        <NewStayDates
          mt={10}
          newCheckIn={startDate}
          newCheckOut={endDate}
          openCancellationPolicyModal={handleTopCancellationPolicyButtonClick}
        />

        <PaymentAdjustmentDetails />

        {type !== 'NO_PAYMENT' && <HorizontalRule m={0} mt={4} />}

        <TermsAndConditionsAgreement
          onAcceptTermsChange={handleTermsAndConditionsCheckboxChange}
          onClickCancellationPolicy={handleBottomCancellationPolicyButtonClick}
          onPrivacyAndSecurityClick={() => track.privacyAndSecurityLink()}
          onTermsAndConditionsClick={() => track.termsAndConditionsLink()}
          mt={6}
        />

        <ButtonGroup
          mt={10}
          desktopBackLabel="Back"
          mobileBackLabel="Back"
          withBackIcon
          onClickBack={handleBackToEdit}
          continueLabel={continueLabel}
          onClickContinue={handleAccept}
          CustomContinueButton={errorCode ? CallToChange : null}
          continueButtonProps={{ loading: false, disabled: continueButtonDisabled, width: ['100%', '224px'] }}
        />
      </TwoColumnWithBookingSummary>
      {showDisclaimer ? (
        <Container mt={16} mb={[20, 16]}>
          <FeesDisclaimer.Body />
          <VoucherDisclaimer.Body />
          <RefundDisclaimer.Body />
        </Container>
      ) : null}
    </>
  );
};

export default ReviewQuote;
